/* eslint-disable react/jsx-max-depth */

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import invariant from 'tiny-invariant';
import Page from '../../components/page/Page';
import Icon from '../../components/icon/Icon';
import text, { formatCurrency } from '../../text';
import {
  Job,
  Selection,
  useGetJobQuery,
  useQuoteOrderMutation,
} from '../../redux/rtk';
import useUrlParam from '../hooks/useUrlParam';
import { useDispatch, useSelector } from 'react-redux';
import { Charge } from '../services/coverage/workers/quote.worker';
import {
  getJob,
  getMembershipPrices,
  getUserProfile,
  setMembershipPriceId,
  showModal,
} from '../../redux/actions';
import { QUOTING_FAILURE_MODAL } from '../../components/modal/QuotingFailureModal';
import { MEMBERSHIP } from '../../constants/membership';
import useUser from '../hooks/useUser';
import { isMembershipPriceId } from '../../utilities/membership';
import ProductInvoice from '../components/Payment/ProductInvoice';
import MembershipInvoice from '../components/Payment/MembershipInvoice';
import { InvoiceTotal } from '../../pages/billing/Invoice/InvoiceTotal';
import ForQuotation from '../components/Payment/ForQuotation';
import OrderWizard from '../../pages/billing/OrderWizard/OrderWizard';
import PaymentProvider from '../../pages/billing/PaymentContext';
import { useRedirect } from '../../hooks/useRedirect';
import { isUserGovernment } from '../../utilities/user';
import { GST } from '../../constants/price';
import CtaButton from '../../components/RightMenu/CtaButton';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { PaymentContext as OtherPaymentContext } from '../../pages/billing/PaymentContext';
import { jobsConstants } from '../../redux/constants';

const PaymentContext = createContext<
  | {
      job?: Job;
      isJobLoading: boolean;
      cartItemsAutomatic?: Charge[];
      numBillableItems: number;
      cardHeading: string;
    }
  | undefined
>(undefined);

export const usePaymentContext = () => {
  const ctx = useContext(PaymentContext);
  invariant(
    ctx,
    'PaymentContext must be used within a PaymentContext.Provider'
  );
  return ctx;
};

const PaymentPage = ({
  jobId,
  job,
  isJobLoading,
}: {
  jobId: number;
  job?: Job & { user_order?: { selections: Selection[] } };
  isJobLoading: boolean;
}) => {
  const redirect = useRedirect();
  const currentUser = useUser();

  const dispatch = useDispatch();
  const [quoteOrderMutation] = useQuoteOrderMutation();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy
  const { membershipPriceId, membershipPrices } = useSelector((state: any) => ({
    membershipPriceId: state.order.membershipPriceId,
    membershipPrices: state.profileReducer.membershipPrices,
  }));

  const [cardHeading] = useState(text('estimate'));

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy
  const otherPaymentContext: any = useContext(OtherPaymentContext);
  const {
    states: { activeWizardStep },
    refs: { wizzardFunctions },
  } = otherPaymentContext;

  const cartItemsAutomatic = useMemo(
    () =>
      job?.quote?.charges.filter(
        (charge) => charge.details?.delivery_method === 'automatic'
      ),
    [job?.quote]
  );

  const selectedMembership = useMemo(
    () =>
      membershipPrices.value?.find(
        (membership: { id: string }) => membership.id === membershipPriceId
      ),
    [membershipPriceId, membershipPrices]
  );

  const numBillableItems = useMemo(
    () =>
      [cartItemsAutomatic?.length, selectedMembership].filter(Boolean).length,
    [cartItemsAutomatic?.length, selectedMembership]
  );

  useEffect(() => {
    dispatch(setMembershipPriceId(MEMBERSHIP.NO_MEMBERSHIP));
    dispatch(getMembershipPrices());
    dispatch(getUserProfile());
    dispatch(getJob(jobId));
  }, [dispatch, jobId]);

  const isFreeUser = useMemo(() => {
    if (isUserGovernment(currentUser.profile)) return false;
    return !currentUser.hasValidMembership || currentUser.isUserUnderTrial;
  }, [currentUser]);

  const isInProjectStep = activeWizardStep && activeWizardStep.step === 2;

  const memberBuyNowDiscount = useMemo(() => {
    if (!job?.quote) {
      return null;
    }
    const { gst = GST, price, currency } = job.quote;
    return formatCurrency(
      price.member_automatic_discount_total * (1 + gst / 100),
      currency.name,
      currency.scale_factor,
      {},
      true
    );
  }, [job?.quote]);

  const isMembershipCtaVisible =
    isFreeUser &&
    isInProjectStep &&
    !isMembershipPriceId(membershipPriceId) && // if no membership selected from previous step (noMembership)
    memberBuyNowDiscount;

  const quoteRequestedRef = useRef<string | null>(null);
  // Run quote once when in Payment page, to consolidate totals are accurate for the current user
  useEffect(() => {
    const handleQuoteOrder = async () => {
      if (!job?.id || !job.user_order?.selections || !currentUser.profile?.id) {
        return;
      }
      const requestKey = `${job.id}-${currentUser.profile.id}`;
      if (quoteRequestedRef.current !== requestKey) {
        quoteRequestedRef.current = requestKey;

        try {
          const response = await quoteOrderMutation({
            jobId,
            selections: job.user_order.selections,
            includeFreeTierDiscount: true,
            property: null,
            shouldSetUpdater: true,
            invalidateProject: true,
          }).unwrap();

          // update legacy state
          dispatch({
            type: jobsConstants.QUOTE_ORDER_SUCCESS,
            payload: response,
          });
        } catch (error) {
          console.error('Quote order failed:', error);
          dispatch(showModal(QUOTING_FAILURE_MODAL));
        }
      }
    };

    handleQuoteOrder();
  }, [
    currentUser.profile?.id,
    dispatch,
    job?.id,
    job?.user_order?.selections,
    jobId,
    quoteOrderMutation,
  ]);

  return (
    <PaymentContext.Provider
      value={{
        job,
        isJobLoading,
        cartItemsAutomatic,
        numBillableItems,
        cardHeading,
      }}
    >
      {/* @ts-expect-error Page */}
      <Page className='Payment'>
        <div className='main-wrapper'>
          <div className='invoice-wrapper'>
            <div className='invoice-section'>
              <div className='d-flex'>
                <Link
                  onClick={() => redirect.goBack()}
                  className='d-flex align-items-center'
                  to='#'
                >
                  <Icon icon='caret-left' />
                  {text('backTo2DMap')}:
                </Link>
              </div>

              {cartItemsAutomatic?.length ? (
                <>
                  <p className='invoice-title text-uppercase font-16 mb-3'>
                    {text('buyNow')}:
                  </p>
                  <ProductInvoice />
                </>
              ) : null}

              {isFreeUser && !currentUser.isStaff ? (
                <MembershipInvoice
                  numOrder={numBillableItems}
                  isFromOrderWizard={true}
                />
              ) : null}

              {cartItemsAutomatic?.length ||
              (!currentUser.hasValidMembership &&
                !currentUser.isStaff &&
                isMembershipPriceId(membershipPriceId)) ? (
                <InvoiceTotal
                  quote={job?.quote}
                  currentUser={currentUser}
                  membershipPriceId={membershipPriceId}
                  selectedMembership={selectedMembership}
                />
              ) : (
                <div className='mt-3' />
              )}

              {isMembershipCtaVisible ? (
                <CtaButton
                  className='mb-2'
                  show
                  header={text('membership')}
                  description={text('ctaMembershipSubtitle', {
                    amount: `-${memberBuyNowDiscount}`,
                  })}
                  titleClassNames='pl-2 font-14'
                  subTitleClassNames='pl-2 pink font-14 font-italic mt-2'
                  isTertiary
                  // @ts-expect-error prop
                  tooltip={
                    <OverlayTrigger
                      trigger={['hover', 'focus']}
                      placement='top'
                      overlay={
                        <Tooltip id='other-charge-tooltip'>
                          {text('ctaMembershipTooltip')}
                        </Tooltip>
                      }
                    >
                      <Icon
                        icon='question'
                        className='other-charge-icon'
                        // @ts-expect-error prop
                        style={{ pointerEvents: 'auto' }}
                      />
                    </OverlayTrigger>
                  }
                  handleClick={() => {
                    if (wizzardFunctions.current) {
                      dispatch(
                        setMembershipPriceId(MEMBERSHIP.YEARLY.PRICE_ID)
                      );
                      wizzardFunctions.current.previousStep();
                    }
                  }}
                />
              ) : null}

              <ForQuotation numOrder={numBillableItems} />
            </div>
          </div>

          <div className='order-wizard'>
            <div className='section'>
              <OrderWizard />
            </div>
          </div>
        </div>
      </Page>
    </PaymentContext.Provider>
  );
};

const PaymentPageProvider = () => {
  const redirect = useRedirect();
  const jobId = useUrlParam('jobId', {
    int: true,
    onParseError: () => redirect.goBack(),
  });
  const { data: job, isLoading: isJobLoading } = useGetJobQuery(jobId);
  return (
    <PaymentProvider job={job}>
      <PaymentPage jobId={jobId} job={job} isJobLoading={isJobLoading} />
    </PaymentProvider>
  );
};

export default PaymentPageProvider;
